import { useTraderText } from '../context/TraderLanguageContext';
import { useMarketClock } from '../hooks/useBinanceHistory';
import { isBinancePair,freshBinanceQuote } from '../utils/binance';
import { BinanceStatus,BinanceFXNotice } from './BinanceStatus';
import {AccountPriceNotice} from './AccountPriceNotice';
/**
 * New Order Modal Component
 * Displays when clicking on an instrument to place a trade
 * Matches SiRiX/MT5 design with Sell/Buy, Market/Pending, Size, SL/TP
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { orderIntent } from '../utils/orderIntent';
import { useOrderPreview } from '../hooks/useOrderPreview';
import { X, Info, Minus, Plus, ChevronDown, AlertCircle } from 'lucide-react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderAuth } from '../context/TraderAuthContext';
import { scopedQuote } from '../utils/quotes';
import { useWebSocket } from '../../context/WebSocketContext';
import { toast } from 'sonner';
import './new-order-compact.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Country flags by instrument type/region
const FLAGS = {
  'EUR': '🇪🇺', 'USD': '🇺🇸', 'GBP': '🇬🇧', 'JPY': '🇯🇵', 'CHF': '🇨🇭',
  'AUD': '🇦🇺', 'CAD': '🇨🇦', 'NZD': '🇳🇿', 'TRY': '🇹🇷',
  'BTC': '₿', 'ETH': 'Ξ', 'XRP': '✕', 'SOL': '◎',
  'XAU': '🥇', 'XAG': '🥈',
  'US1': '🇺🇸', 'US5': '🇺🇸', 'US3': '🇺🇸', 'GER': '🇩🇪', 'UK1': '🇬🇧', 'JP2': '🇯🇵',
};

// Commodity symbols that should show oil/commodity icons, not Turkish flag
const COMMODITY_SYMBOLS = ['USOIL', 'UKOIL', 'OIL', 'WTI', 'BRENT', 'NATGAS', 'GAS', 'HEATING', 
                           'SOYOIL', 'PALMOIL', 'COPPER', 'WHEAT', 'CORN', 'COFFEE', 'SUGAR', 'COTTON'];

const getInstrumentFlag = (symbol, category) => {
  if (!symbol) return '📈';
  const upper = symbol.toUpperCase().replace('.IS', '');
  
  // COMMODITIES CHECK FIRST - oil, gas, metals, agriculture
  if (category === 'commodities' || COMMODITY_SYMBOLS.some(c => upper.includes(c))) {
    if (upper.includes('OIL') || upper.includes('WTI') || upper.includes('BRENT')) return '🛢️';
    if (upper.includes('GAS') || upper.includes('NATGAS')) return '🔥';
    if (upper.includes('XAU') || upper.includes('GOLD')) return '🥇';
    if (upper.includes('XAG') || upper.includes('SILVER')) return '🥈';
    if (upper.includes('XPT') || upper.includes('PLAT')) return '⬜';
    if (upper.includes('XPD') || upper.includes('PALL')) return '⬛';
    if (upper.includes('COPPER')) return '🟤';
    return '📦'; // Generic commodity
  }
  
  // Check for Turkish stocks (BIST) - MUST have .IS suffix or be explicitly BIST
  if (symbol.includes('.IS') || category === 'bist') {
    return '🇹🇷';
  }
  
  // Check for forex pairs (contains / or starts with known currency codes)
  for (const [code, flag] of Object.entries(FLAGS)) {
    if (upper.startsWith(code)) {
      return flag;
    }
  }
  
  // Check for crypto
  if (upper.includes('BTC')) return '₿';
  if (upper.includes('ETH')) return 'Ξ';
  if (upper.includes('XRP')) return '✕';
  if (upper.includes('SOL')) return '◎';
  if (upper.includes('ADA') || upper.includes('DOGE') || upper.includes('LTC')) return '🪙';
  
  // Check for indices
  if (upper.includes('US100') || upper.includes('NAS') || upper.includes('US500') || upper.includes('US30')) return '🇺🇸';
  if (upper.includes('GER') || upper.includes('DAX')) return '🇩🇪';
  if (upper.includes('UK100') || upper.includes('FTSE')) return '🇬🇧';
  if (upper.includes('JP225') || upper.includes('NIKKEI')) return '🇯🇵';
  if (upper.includes('XU100') || upper.includes('BIST')) return '🇹🇷';
  
  return '📈';
};

const SIZE_OPTIONS = [0.01, 0.05, 0.1, 0.5, 1, 2, 5, 10];

const NewOrderModal = ({ isOpen, onClose, instrument }) => {
  const tx = useTraderText();
  const orderKey=useRef(null);
  useEffect(()=>{if(isOpen)orderKey.current=null;},[isOpen]);
  const {user}=useTraderAuth();
  const { isDark } = useTraderTheme();
  const { prices, subscribe, isConnected } = useWebSocket();
  
  const [side, setSide] = useState('sell'); // 'sell' or 'buy'
  useEffect(()=>{if(isOpen)setSide(instrument?.quickTradeSide||instrument?.side||'sell');},[isOpen,instrument?.symbol,instrument?.quickTradeSide,instrument?.side]);
  const [orderType, setOrderType] = useState('market'); // 'market' or 'pending'
  const [size, setSize] = useState(0.01);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [stopLossEnabled, setStopLossEnabled] = useState(false);
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [pendingPrice, setPendingPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [marketStatus, setMarketStatus] = useState({ is_open: true, reason: '' });
  const [checkingMarket, setCheckingMarket] = useState(false);
  const [showInstrumentInfo, setShowInstrumentInfo] = useState(false);
  const [showTradingHours, setShowTradingHours] = useState(false);

  const symbol = instrument?.symbol || 'EUR/USD';
  const livePrice = scopedQuote(instrument, prices[symbol]);
  const isBinance = !instrument?.is_virtual && isBinancePair(symbol);
  const clock = useMarketClock(isBinance);
  const binanceAvailable = !isBinance || freshBinanceQuote(prices[symbol],isConnected,clock);
  
  // Get current bid/ask from WebSocket
  const bid = isBinance ? (binanceAvailable?livePrice?.bid||0:0) : livePrice?.bid || instrument?.bid || 0;
  const ask = isBinance ? (binanceAvailable?livePrice?.ask||0:0) : livePrice?.ask || instrument?.ask || 0;
  
  // Calculate margin and pip value (simplified)
  const configuredLeverage = Math.min(instrument?.effective_leverage || instrument?.leverage_max || 100,user?.effective_leverage || user?.leverage || 100);
  const contractSize = instrument?.contract_size ?? instrument?.lot_size ?? 1;
  
  // Ensure size is always a number for calculations
  const numericSize = useMemo(() => {
    if (typeof size === 'string') {
      const parsed = parseFloat(size);
      return isNaN(parsed) ? 0.01 : parsed;
    }
    return size;
  }, [size]);
  
  const preview=useOrderPreview(isOpen,{symbol,side,type:orderType,volume:numericSize,...(orderType==='pending'&&Number(pendingPrice)>0?{price:Number(pendingPrice)}:{}),stop_loss:stopLossEnabled&&stopLoss?Number(stopLoss):null,take_profit:takeProfitEnabled&&takeProfit?Number(takeProfit):null});
  const margin=preview.data?.additional_margin;
  const leverage=preview.data?.leverage??configuredLeverage;
  
  const pipValue = preview.data?.pip_value;

  // Instrument specifications for the info popup
  const instrumentSpecs = useMemo(() => {
    // Get current time in Turkey timezone
    const now = new Date();
    const turkeyTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
    
    const isForex = symbol.includes('/') || symbol.includes('USD') || symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('JPY') || symbol.includes('CHF') || symbol.includes('AUD') || symbol.includes('CAD') || symbol.includes('NZD');
    const isCrypto = isBinancePair(symbol) || symbol.includes('BTC') || symbol.includes('ETH') || symbol.includes('XRP') || symbol.includes('LTC');
    const isBIST = symbol.includes('.IS') || symbol.includes('BIST') || symbol.includes('-IST') || instrument?.category === 'BIST' || instrument?.category === 'STOCKS_BIST';
    const isGold = symbol.includes('XAU');
    const isOil = symbol.includes('OIL') || symbol.includes('WTI') || symbol.includes('BRENT');
    const isIndex = symbol.includes('US1') || symbol.includes('US5') || symbol.includes('NAS') || symbol.includes('US30') || symbol.includes('GER') || symbol.includes('DAX') || symbol.includes('UK1') || symbol.includes('XU100') || instrument?.category === 'INDICES';
    const cleanSymbol = symbol.replace('.IS', '').replace('-IST', '').replace('/', '');
    const isEUStock = instrument?.category === 'EU_STOCKS' || symbol.endsWith('.DE') || symbol.endsWith('.PA') || symbol.endsWith('.SW') || symbol.endsWith('.AS') || ['GERMANY', 'FRANCE', 'NETHERLANDS', 'SWITZERLAND'].includes(instrument?.exchange);
    const isTurkishStock = !isEUStock && !isForex && !isCrypto && !isGold && !isOil && !isIndex && 
                          (isBIST || (cleanSymbol.length <= 6 && /^[A-Z0-9]+$/.test(cleanSymbol) && instrument?.exchange === 'BIST'));
    const isUSStock = instrument?.category === 'US_STOCKS' || instrument?.exchange === 'NASDAQ' || instrument?.exchange === 'NYSE' || (!isForex && !isCrypto && !isBIST && !isTurkishStock && !isGold && !isOil && !isIndex && !isEUStock && (instrument?.type === 'stock' || instrument?.type === 'stocks' || /^[A-Z]{1,5}$/.test(cleanSymbol)));
    
    // Determine security name/category
    let securityName = 'Forex';
    let tradingHours = { weekdays: '00:00 - 23:59', weekend: 'Closed' };
    
    if (isCrypto) {
      securityName = 'Cryptocurrency';
      tradingHours = { weekdays: '24/7', weekend: '24/7', note: 'Always Open' };
    } else if (isBIST || isTurkishStock) {
      securityName = 'BIST Stocks';
      tradingHours = { weekdays: '10:00 - 18:00', weekend: 'Closed', timezone: 'Turkey Time' };
    } else if (isEUStock) {
      securityName = 'EU Stocks';
      tradingHours = { weekdays: '09:00 - 17:30', weekend: 'Closed', timezone: 'CET / Berlin (10:00 - 18:30 TR)' };
    } else if (isUSStock) {
      securityName = 'US Stocks';
      tradingHours = { weekdays: '09:30 - 16:00', weekend: 'Closed', timezone: 'ET / New York (16:30 - 23:00 TR)' };
    } else if (isGold) {
      securityName = 'Precious Metals';
      tradingHours = { weekdays: '01:00 - 23:59', weekend: 'Closed', timezone: 'Turkey Time' };
    } else if (isOil) {
      securityName = 'Energy';
      tradingHours = { weekdays: '01:00 - 23:59', weekend: 'Closed', timezone: 'Turkey Time' };
    } else if (isIndex) {
      securityName = 'Indices';
      if (symbol.includes('XU100')) {
        tradingHours = { weekdays: '10:00 - 18:00', weekend: 'Closed', timezone: 'Turkey Time' };
      } else if (symbol.includes('US') || symbol.includes('NAS')) {
        tradingHours = { weekdays: '16:30 - 23:00', weekend: 'Closed', timezone: 'Turkey Time' };
      } else if (symbol.includes('GER') || symbol.includes('DAX')) {
        tradingHours = { weekdays: '10:00 - 19:30', weekend: 'Closed', timezone: 'Turkey Time' };
      } else if (symbol.includes('UK')) {
        tradingHours = { weekdays: '10:00 - 19:30', weekend: 'Closed', timezone: 'Turkey Time' };
      } else {
        tradingHours = { weekdays: '11:00 - 00:00', weekend: 'Closed', timezone: 'Turkey Time' };
      }
    } else if (isForex) {
      const majors = ['EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD'];
      const normalized = symbol.replace('/', '');
      if (majors.includes(normalized)) {
        securityName = 'Forex Majors';
      } else if (symbol.includes('TRY') || symbol.includes('ZAR') || symbol.includes('MXN') || symbol.includes('PLN')) {
        securityName = 'Forex Exotic';
      } else {
        securityName = 'Forex Minors';
      }
      tradingHours = { weekdays: '01:00 - 23:59', weekend: 'Closed', note: 'Sun 01:00 - Fri 23:59', timezone: 'Turkey Time' };
    }
    
    // Contract size varies by instrument type
    let contractSizeSpec = 100000;
    if (isCrypto) contractSizeSpec = 1;
    else if (isBIST || isTurkishStock) contractSizeSpec = 1;
    else if (isGold) contractSizeSpec = 100;
    else if (isOil) contractSizeSpec = 1000;
    else if (isIndex) contractSizeSpec = 1;
    contractSizeSpec = instrument?.contract_size ?? instrument?.lot_size ?? contractSizeSpec;
    
    // Pip size
    let pipSize = 4;
    if (symbol.includes('JPY')) pipSize = 2;
    else if (isCrypto) pipSize = 2;
    else if (isBIST || isTurkishStock) pipSize = 2;
    else if (isGold) pipSize = 2;
    else if (isIndex) pipSize = 1;
    
    // Trading limits
    const tradingMin = isCrypto ? 0.001 : ((isBIST || isTurkishStock) ? 1 : 0.01);
    const tradingMax = isCrypto ? 10 : 100;
    const tradingStep = isCrypto ? 0.001 : ((isBIST || isTurkishStock) ? 1 : 0.01);
    
    // Leverage
    let leverageSpec = instrument?.leverage_max || leverage || 100;
    if (isCrypto) leverageSpec = 10;
    else if (isBIST || isTurkishStock) leverageSpec = 10;
    else if (isGold) leverageSpec = 100;
    else if (isIndex) leverageSpec = 50;
    else if (isForex) leverageSpec = 200;
    
    return {
      lastUpdate: turkeyTime.toLocaleDateString(tx('en-US')) + ' ' + turkeyTime.toLocaleTimeString(tx('en-US'), { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      instrument: symbol.replace('/', ''),
      securityName,
      pipSize,
      stopLevel: 0,
      maxExposure: 0,
      leverage: leverageSpec,
      contractSize: contractSizeSpec.toLocaleString(tx('en-US')),
      tradingMin,
      tradingMax,
      tradingStep,
      swapLong: instrument?.swap_long || 0,
      swapShort: instrument?.swap_short || 0,
      swapType: 'By Points',
      isMarketOpen: marketStatus.is_open,
      tradingHours,
      isBIST: isBIST || isTurkishStock,
      isCrypto,
    };
  }, [symbol, instrument, leverage, marketStatus.is_open, tx]);

  // Subscribe to instrument price
  useEffect(() => {
    if (isOpen && symbol) {
      subscribe(symbol);
    }
  }, [isOpen, symbol, subscribe]);

  // Check market status when modal opens
  useEffect(() => {
    if (isOpen && symbol) {
      checkMarketStatus();
    }
  }, [isOpen, symbol]);

  const checkMarketStatus = async () => {
    setCheckingMarket(true);
    try {
      const token = localStorage.getItem('trader_token');
      const statusURL = isBinance ? `${API_BASE}/api/trader/market-data/quote?symbol=${encodeURIComponent(symbol)}` : `${API_BASE}/api/sessions/check/${encodeURIComponent(symbol)}`;
      const response = await fetch(statusURL, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        const data = await response.json();
        const status = isBinance ? {is_open:Boolean(data.trading_session?.tradeable),reason:data.trading_session?.reason} : data;
        const isOpen = Boolean(status.is_open);
        setMarketStatus({
          is_open: isOpen,
          reason: status.reason || (isOpen ? '' : 'Market is not currently open to trade'),
        });
        if (!isOpen) {
          // When market is closed, automatically offer Schedule Trade (pending order)
          setOrderType('pending');
          setPendingPrice(prev => prev || String(side === 'buy' ? (ask || instrument?.ask || '') : (bid || instrument?.bid || '')));
        }
      } else if(isBinance) {
        setMarketStatus({is_open:false,reason:tx('Binance status is unavailable')});
      }
    } catch (error) {
      console.error('Failed to check market status:', error);
      setMarketStatus({ is_open: !isBinance, reason: isBinance?tx('Binance status is unavailable'):'' });
    } finally {
      setCheckingMarket(false);
    }
  };

  const formatPrice = (price) => {
    if (!price || isNaN(price)) return '—';
    const forex=instrument?.type==='forex'||/^(EUR|GBP|USD|AUD|NZD|CAD|CHF|JPY|TRY|ZAR|MXN|PLN)\/?(EUR|GBP|USD|AUD|NZD|CAD|CHF|JPY|TRY|ZAR|MXN|PLN)$/.test(symbol);
    const decimals=forex?(symbol.endsWith('JPY')?3:5):(Number(price)<1?5:2);
    return Number(price).toLocaleString(tx('en-US'),{minimumFractionDigits:decimals,maximumFractionDigits:decimals});
  };

  const formatMargin = (value) => {
    if(value===undefined||value===null)return preview.loading?'…':'—';
    return Number(value).toLocaleString(tx('en-US'),{style:'currency',currency:user?.currency||'USD',minimumFractionDigits:2,maximumFractionDigits:2});
  };

  const handleSizeChange = (delta) => {
    const currentSize = typeof size === 'string' ? parseFloat(size) || 0.01 : size;
    const newSize = Math.max(0.01, Math.min(100, Math.round((currentSize + delta) * 100) / 100));
    setSize(newSize);
  };

  const handleSubmitOrder = async () => {
    if (!marketStatus.is_open && orderType === 'market') {
      toast.error(tx(marketStatus.reason || 'Market is closed. Please switch to Pending / Schedule Trade.'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('trader_token');
      const price = side === 'buy' ? ask : bid;
      
      const orderData = {
        symbol,
        side,
        type: orderType,
        volume: numericSize,
        price: orderType === 'pending' ? parseFloat(pendingPrice) : price,
        stop_loss: stopLossEnabled && stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfitEnabled && takeProfit ? parseFloat(takeProfit) : null,
      };

      const response = await fetch(`${API_BASE}/api/trader/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(orderIntent(orderKey,orderData)),
      });

      if (response.ok) {
        const result = await response.json();
        orderKey.current=null;
        if (orderType === 'pending') {
          toast.success(tx(`Order Scheduled: ${side.toUpperCase()} ${numericSize} ${symbol} @ ${formatPrice(result.order?.price || pendingPrice)} (Pending market open)`));
        } else {
          toast.success(tx(`Order placed: ${side.toUpperCase()} ${numericSize} ${symbol} @ ${formatPrice(result.order?.price || price)}`));
        }
        window.dispatchEvent(new Event('trader-data-changed'));
        onClose();
      } else {
        const error = await response.json();
        toast.error(tx(Array.isArray(error.detail)?error.detail.map(item=>item.msg).join('; '):error.detail || 'Failed to place order'));
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(tx('Failed to place order. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const executionPrice = side === 'buy' ? ask : bid;

  return (
    <div 
      className="tr-order-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="new-order-backdrop"
      style={{ touchAction: 'none' }}
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className={`tr-order-ticket w-full max-w-md mx-4 rounded-xl shadow-2xl overflow-hidden ${
          isDark ? 'bg-[#1a2332] text-white' : 'bg-white text-gray-900'
        }`}
        style={{ touchAction: 'manipulation', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="new-order-modal"
        data-theme={isDark ? 'dark' : 'light'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-order-title"
      >
        {/* Header */}
        <div className={`tr-order-header flex items-center justify-between px-4 py-3 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <span id="new-order-title" data-testid="new-order-title" className="tr-order-title text-lg font-semibold">{tx("New Order")}</span>
          <button 
            onClick={onClose}
            className={`p-1 rounded-lg transition-colors ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
            data-testid="close-order-modal"
            aria-label={tx('Close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Instrument Info */}
        <div className="tr-order-body px-4 py-4">
          <div className="tr-order-instrument flex items-center justify-between mb-4">
            <div className="tr-order-identity flex items-center gap-3">
              <span className="tr-order-flag text-3xl" aria-hidden="true">{getInstrumentFlag(symbol, instrument?.category)}</span>
              <div className="tr-order-instrument-copy">
                <div className="tr-order-symbol-line flex items-center gap-2">
                  <span className="tr-order-symbol font-bold text-lg" data-testid="order-instrument-symbol">{symbol}</span>
                  <button 
                    onClick={() => setShowInstrumentInfo(!showInstrumentInfo)}
                    className={`p-1 rounded-full transition-colors ${
                      showInstrumentInfo 
                        ? 'bg-blue-500 text-white' 
                        : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                    data-testid="instrument-info-btn"
                    title={tx("Instrument specifications")}
                  >
                    <Info size={16} className={showInstrumentInfo ? 'text-white' : 'text-gray-400'} />
                  </button>
                </div>
                <span data-testid="order-instrument-name" className={`tr-order-instrument-name text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {instrument?.name || symbol}
                </span>
              </div>
            </div>
            <div className="tr-order-quotes text-right" data-testid="order-live-quotes">
              <div className="flex items-center gap-2 text-sm">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Ask:")}</span>
                <span className="font-mono font-bold text-green-500" data-testid="order-live-ask">{formatPrice(ask)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Bid:")}</span>
                <span className="font-mono font-bold text-red-400" data-testid="order-live-bid">{formatPrice(bid)}</span>
              </div>
            </div>
          </div>

          {/* Instrument Specifications Popup - Compact */}
          {showInstrumentInfo && (
            <div className={`mb-3 rounded-lg border shadow-lg overflow-hidden text-xs ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`} data-testid="instrument-info-popup">
              <div className={`px-3 py-2 border-b ${isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-100 bg-gray-50'}`}>
                <h3 className="font-bold text-center text-sm">{instrumentSpecs.instrument}</h3>
                <p className={`text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {tx(instrumentSpecs.securityName)}
                </p>
              </div>
              
              {/* Two-column grid for compact display */}
              <div className="p-2 grid grid-cols-2 gap-x-4 gap-y-1">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Pip size")}</span>
                  <span className="font-medium">{instrumentSpecs.pipSize}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Leverage")}</span>
                  <span className="font-medium">{instrumentSpecs.leverage}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Contract")}</span>
                  <span className="font-medium">{instrumentSpecs.contractSize}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Stop lvl")}</span>
                  <span className="font-medium">{instrumentSpecs.stopLevel}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Min lot")}</span>
                  <span className="font-medium">{instrumentSpecs.tradingMin}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Max lot")}</span>
                  <span className="font-medium">{instrumentSpecs.tradingMax}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Swap L")}</span>
                  <span className="font-medium">{instrumentSpecs.swapLong}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Swap S")}</span>
                  <span className="font-medium">{instrumentSpecs.swapShort}</span>
                </div>
              </div>
              
              <div className={`px-3 py-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'} flex items-center justify-between`}>
                <button 
                  onClick={() => setShowTradingHours(!showTradingHours)}
                  className={`flex items-center gap-1 text-xs font-medium ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >{tx("Hours ")}<ChevronDown size={12} className={`transition-transform ${showTradingHours ? 'rotate-180' : ''}`} />
                </button>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  instrumentSpecs.isMarketOpen 
                    ? 'bg-green-500/20 text-green-500' 
                    : 'bg-red-500/20 text-red-500'
                }`}>
                  {tx(instrumentSpecs.isMarketOpen ? 'Open' : 'Closed')}
                </span>
              </div>
              
              {showTradingHours && (
                <div className={`px-3 py-2 border-t ${isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="text-xs space-y-0.5">
                    {instrumentSpecs.isCrypto ? (
                      <div className="flex justify-between">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>24/7</span>
                        <span className="font-medium text-green-500">{tx("Always Open")}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Mon-Fri")}</span>
                          <span className="font-medium">{instrumentSpecs.tradingHours.weekdays}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Sat-Sun")}</span>
                          <span className={`font-medium ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                            {tx(instrumentSpecs.tradingHours.weekend)}
                          </span>
                        </div>
                        {instrumentSpecs.tradingHours.note && (
                          <div className="flex justify-between">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Note")}</span>
                            <span className="font-medium text-cyan-500">{tx(instrumentSpecs.tradingHours.note)}</span>
                          </div>
                        )}
                        {instrumentSpecs.tradingHours.timezone && (
                          <div className={`text-center mt-1 pt-1 border-t ${isDark ? 'border-gray-600 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                            {tx(instrumentSpecs.tradingHours.timezone)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Market Status Warning */}
          {isBinance&&<BinanceStatus quote={prices[symbol]} id="binance-order-status"/>}
          <AccountPriceNotice quote={prices[symbol]} id="account-price-order-notice"/>
          {isBinance&&<BinanceFXNotice snapshot={preview.data?.fx_snapshot} id="binance-order-fx-notice"/>}
          {!marketStatus.is_open && (
            <div className="tr-order-market-warning mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2.5" role="alert" data-testid="market-closed-warning">
              <AlertCircle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-semibold text-amber-500 block">
                  {tx("Market Closed")} — {tx(marketStatus.reason || 'Trading session is closed')}
                </span>
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {tx("Instant market execution is disabled. You can prepare and schedule an order below to be triggered when the market opens.")}
                </span>
              </div>
            </div>
          )}

          {/* Sell/Buy Toggle */}
          <div className="tr-order-side grid grid-cols-2 gap-2 mb-4">
            <button
              onClick={() => setSide('sell')}
              className={`py-3 rounded-lg font-bold text-lg transition-all ${
                side === 'sell'
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
              }`}
              data-testid="order-sell-btn"
              aria-pressed={side === 'sell'}
            >{tx("Sell ")}</button>
            <button
              onClick={() => setSide('buy')}
              className={`py-3 rounded-lg font-bold text-lg transition-all ${
                side === 'buy'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
              }`}
              data-testid="order-buy-btn"
              aria-pressed={side === 'buy'}
            >{tx("Buy ")}</button>
          </div>

          {/* Market/Pending Toggle */}
          <div className="tr-order-type grid grid-cols-2 gap-2 mb-4">
            <button
              onClick={() => {
                if (marketStatus.is_open) {
                  setOrderType('market');
                } else {
                  toast.info(tx('Market is currently closed. Use Schedule Trade to place an order for when the market opens.'));
                }
              }}
              disabled={!marketStatus.is_open}
              className={`py-2.5 rounded-lg font-medium transition-all ${
                orderType === 'market'
                  ? isDark ? 'bg-gray-600 text-white' : 'bg-gray-800 text-white'
                  : isDark ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-400'
              } ${!marketStatus.is_open ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="order-market-btn"
              aria-pressed={orderType === 'market'}
              title={!marketStatus.is_open ? tx("Market Closed") : ""}
            >{tx("Market")}{!marketStatus.is_open ? ` (${tx('Closed')})` : ''}</button>
            <button
              onClick={() => {
                setOrderType('pending');
                if (!pendingPrice && executionPrice) {
                  setPendingPrice(String(executionPrice));
                }
              }}
              className={`py-2.5 rounded-lg font-medium transition-all ${
                orderType === 'pending'
                  ? !marketStatus.is_open
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md'
                    : isDark ? 'bg-gray-600 text-white' : 'bg-gray-800 text-white'
                  : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}
              data-testid="order-pending-btn"
              aria-pressed={orderType === 'pending'}
            >{tx(!marketStatus.is_open ? "Schedule Trade" : "Pending")}</button>
          </div>

          {/* Pending Price (only for pending orders) */}
          {orderType === 'pending' && (
            <div className="tr-order-pending-price mb-4">
              <label htmlFor="new-order-pending-price" className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Pending Price ")}</label>
              <input
                id="new-order-pending-price"
                type="number"
                value={pendingPrice}
                onChange={(e) => setPendingPrice(e.target.value)}
                placeholder={formatPrice(executionPrice)}
                className={`w-full px-4 py-2.5 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                data-testid="order-pending-price"
              />
            </div>
          )}

          {/* Size Input */}
          <div className="tr-order-size mb-4">
            <label htmlFor="new-order-size" className={`block text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Size (Lots) ")}</label>
            <div className="tr-order-stepper flex items-center gap-2">
              <button
                onClick={() => handleSizeChange(-0.01)}
                className={`p-3 rounded-lg flex-shrink-0 ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600 active:bg-gray-500' : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300'
                }`}
                data-testid="order-size-minus"
                aria-label={tx('Decrease order size')}
                title={tx('Decrease order size')}
              >
                <Minus size={20} />
              </button>
              
              <div className="relative flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <input
                    id="new-order-size"
                    type="text"
                    inputMode="decimal"
                    value={size}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Only allow valid decimal number patterns
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        setSize(val === '' ? '' : val);
                      }
                    }}
                    onFocus={(e) => {
                      e.stopPropagation();
                      // Select all text on focus for easy editing
                      e.target.select();
                    }}
                    onBlur={(e) => {
                      const numVal = parseFloat(e.target.value);
                      if (isNaN(numVal) || numVal < 0.01) {
                        setSize(0.01);
                      } else if (numVal > 100) {
                        setSize(100);
                      } else {
                        setSize(Math.round(numVal * 100) / 100);
                      }
                    }}
                    className={`w-full px-4 py-3 rounded-lg text-xl text-center ${
                      isDark 
                        ? 'bg-gray-700 text-white border-gray-600' 
                        : 'bg-gray-100 text-gray-900 border-gray-200'
                    } border focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                    style={{ 
                      fontSize: '20px', 
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      WebkitAppearance: 'none', 
                      MozAppearance: 'textfield' 
                    }}
                    data-testid="order-size-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                  <button
                    onClick={() => setShowSizeDropdown(!showSizeDropdown)}
                    data-testid="order-size-dropdown-toggle"
                    aria-label={tx('Size (Lots) ')}
                    aria-expanded={showSizeDropdown}
                    className={`p-3 rounded-lg flex-shrink-0 ${
                      isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <ChevronDown size={18} className="text-gray-400" />
                  </button>
                </div>
                
                {showSizeDropdown && (
                  <div 
                    data-testid="order-size-dropdown"
                    className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto ${
                      isDark ? 'bg-gray-700' : 'bg-white border'
                    }`}
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    {SIZE_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        data-testid={`order-size-option-${opt}`}
                        onClick={(e) => { 
                          e.preventDefault();
                          e.stopPropagation();
                          setSize(opt); 
                          setShowSizeDropdown(false); 
                        }}
                        className={`w-full px-4 py-3 text-left transition-colors ${
                          numericSize === opt 
                            ? 'bg-cyan-500/20 text-cyan-500' 
                            : isDark ? 'hover:bg-gray-600 active:bg-gray-500' : 'hover:bg-gray-100 active:bg-gray-200'
                        }`}
                      >
                        {opt.toFixed(2)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <button
                onClick={() => handleSizeChange(0.01)}
                className={`p-3 rounded-lg ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
                data-testid="order-size-plus"
                aria-label={tx('Increase order size')}
                title={tx('Increase order size')}
              >
                <Plus size={20} />
              </button>
            </div>
            
            {/* Margin & Pip Value */}
            <div data-testid="order-engine-margin-preview" className={`mt-2 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Margin ")}{formatMargin(margin)}{tx(" / Pip value ")}{formatMargin(pipValue)}
            </div>
            {preview.error&&<p role="alert" data-testid="order-engine-preview-error" className="mt-2 text-sm text-amber-600 break-words">{tx(preview.error)}</p>}
            
            {/* Quick-select lot sizes */}
            <div className="mt-3 grid grid-cols-6 gap-1.5" data-testid="lot-quick-select">
              {[0.01, 0.05, 0.1, 0.5, 1, 5].map((lot) => (
                <button
                  key={lot}
                  onClick={() => setSize(lot)}
                  className={`py-2 rounded-lg text-sm font-medium transition-all ${
                    numericSize === lot
                      ? 'bg-cyan-500 text-white shadow-lg scale-105'
                      : isDark 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 active:bg-gray-500' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                  data-testid={`lot-quick-${lot}`}
                  aria-pressed={numericSize === lot}
                >
                  {lot < 1 ? lot.toFixed(2) : lot.toFixed(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Stop Loss & Take Profit */}
          <div className="tr-order-protection grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={stopLossEnabled}
                  onChange={(e) => setStopLossEnabled(e.target.checked)}
                  className="w-5 h-5 rounded border-2 accent-cyan-500"
                  data-testid="order-sl-checkbox"
                />
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{tx("Stop Loss")}</span>
              </label>
              {stopLossEnabled && (
                <input
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder={formatPrice(bid * 0.99)}
                  className={`w-full mt-2 px-3 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-red-500`}
                  data-testid="order-sl-input"
                  aria-label={tx('Stop Loss')}
                />
              )}
            </div>
            
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={takeProfitEnabled}
                  onChange={(e) => setTakeProfitEnabled(e.target.checked)}
                  className="w-5 h-5 rounded border-2 accent-cyan-500"
                  data-testid="order-tp-checkbox"
                />
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{tx("Take Profit")}</span>
              </label>
              {takeProfitEnabled && (
                <input
                  type="number"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder={formatPrice(bid * 1.01)}
                  className={`w-full mt-2 px-3 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-green-500`}
                  data-testid="order-tp-input"
                  aria-label={tx('Take Profit')}
                />
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmitOrder}
            disabled={
              loading || 
              checkingMarket || 
              !(bid>0&&ask>=bid) || 
              numericSize<=0 || 
              (!marketStatus.is_open && orderType === 'market') ||
              (orderType==='pending'&&!(Number(pendingPrice)>0)) || 
              (isBinance&&(preview.loading||Boolean(preview.error)||!preview.data))
            }
            className={`tr-order-submit w-full py-4 rounded-xl font-bold text-lg transition-all ${
              (!marketStatus.is_open && orderType === 'market') || checkingMarket
                ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                : orderType === 'pending'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg'
                  : side === 'sell'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg'
            }`}
            data-testid="order-submit-btn"
            data-side={side}
          >
            {tx(loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />{tx("Processing... ")}
              </span>
            ) : checkingMarket ? (
              'Checking market status...'
            ) : !(bid>0&&ask>=bid) ? (
              'Waiting for quote...'
            ) : (!marketStatus.is_open && orderType === 'market') ? (
              'Market Closed — Switch to Schedule Trade'
            ) : orderType === 'pending' ? (
              `Schedule ${side === 'sell' ? 'Sell' : 'Buy'} ${numericSize.toFixed(2)} @${formatPrice(Number(pendingPrice) || executionPrice)}`
            ) : (
              `Market ${side === 'sell' ? 'Sell' : 'Buy'} ${numericSize.toFixed(2)} @${formatPrice(executionPrice)}`
            ))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewOrderModal;
